import React, { useState, useEffect } from "react";
import { Table, Input, Button, Space, Typography, Modal, Form, Select, InputNumber, message } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import Breadcrumbs from "../../component/Breadcrumbs";
import EmptyState from "../../util/EmptyState";

const { Title } = Typography;

const SellerInventory = () => {
  const [searchProduct, setSearchProduct] = useState("");
  const [searchSeller, setSearchSeller] = useState("");
  const [inventories, setInventories] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const { Option } = Select;

  // fetch lists for selects
  const [productsList, setProductsList] = useState([]);
  const [shopsList, setShopsList] = useState([]);

  const fetchInventories = async () => {
    try {
      setLoading(true);
      const res = await (await import("../../service/SellerInventoryServ")).default.index({});
      let data = res?.data || res || [];
      if (!Array.isArray(data) && data.inventories) data = data.inventories;
      // normalize
      const normalized = (data || []).map((it) => ({
        key: it._id || it.id,
        id: it._id || it.id,
        productName: it.productName || it.productId?.name || (it.productId && it.productId.name),
        productId: it.productId?._id || it.productId?.id || it.productId,
        sellerName: it.sellerName || it.sellerId?.name || it.sellerId?.shopName || (it.sellerId && (it.sellerId.name || it.sellerId.shopName)),
        sellerId: it.sellerId?._id || it.sellerId?.id || it.sellerId,
        stock: it.stock,
        price: it.price,
        updatedAt: it.updatedAt,
      }));
      setInventories(normalized);
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch inventories");
    } finally {
      setLoading(false);
    }
  };

  const fetchProductsAndShops = async () => {
    try {
      const ProductServ = (await import("../../service/ProductServ")).default;
      const ShopServ = (await import("../../service/ShopServ")).default;
      const pRes = await ProductServ.index({ limit: 100 });
      const sRes = await ShopServ.index({ limit: 100 });
      const pData = pRes?.data || pRes || [];
      const sData = sRes?.data || sRes || [];
      setProductsList(Array.isArray(pData) ? pData : []);
      setShopsList(Array.isArray(sData) ? sData : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchInventories();
    fetchProductsAndShops();
  }, []);

  const filteredData = inventories.filter(
    (item) =>
      (item.productName || "").toLowerCase().includes(searchProduct.toLowerCase()) &&
      (item.sellerName || "").toLowerCase().includes(searchSeller.toLowerCase())
  );

  const columns = [
    { title: "ID", dataIndex: "key", key: "id", width: "6%" },
    { title: "Product", dataIndex: "productName", key: "product" },
    { title: "Seller", dataIndex: "sellerName", key: "seller" },
    {
      title: "Stock",
      dataIndex: "stock",
      key: "stock",
      render: (text) => (
        <span style={{ color: text < 50 ? "red" : "inherit", fontWeight: text < 50 ? "bold" : "normal" }}>
          {text}
        </span>
      ),
    },
    { title: "Price", dataIndex: "price", key: "price" },
    {
      title: "Last Updated",
      dataIndex: "updatedAt",
      key: "lastUpdated",
      render: (val) => (val ? new Date(val).toLocaleString() : "-"),
    },
        {
            title: "Actions",
            key: "actions",
            render: (_text, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => openEditModal(record)}
                    >
                        Edit
                    </Button>
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                    >
                        Remove
                    </Button>
                </Space>
            ),
        },
    ];

  let header = "Seller Investors";

  const openCreateModal = () => {
    setIsEditing(false);
    setEditingRecord(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setIsEditing(true);
    setEditingRecord(record);
    form.setFieldsValue({
      productId: record.productId,
      sellerId: record.sellerId,
      stock: record.stock,
      price: record.price,
    });
    setModalOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Remove Inventory",
      content: `Remove inventory for ${record.productName} ? `,
      onOk: async () => {
        try {
          const serv = (await import("../../service/SellerInventoryServ")).default;
          await serv.remove(record.id || record._id);
          message.success("Removed");
          fetchInventories();
        } catch (err) {
          console.error(err);
          message.error("Remove failed");
        }
      },
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        productId: values.productId,
        sellerId: values.sellerId,
        stock: values.stock,
        price: values.price,
      };
      const serv = (await import("../../service/SellerInventoryServ")).default;
      if (isEditing && editingRecord) {
        await serv.update(editingRecord.id || editingRecord._id, payload);
        message.success("Updated");
      } else {
        await serv.create(payload);
        message.success("Created");
      }
      setModalOpen(false);
      fetchInventories();
    } catch (err) {
      console.error(err);
      message.error("Save failed");
    }
  };

  return (
    <div className="pcu-padding-24">
      <div className="pcu-margin-bottom-16">
        <Breadcrumbs />
      </div>

      <div className="main-content-card pcu-bg-white pcu-radius-16 pcu-shadow-sm pcu-overflow-hidden">
        <div className="pcu-padding-x-24 pcu-padding-top-24 pcu-padding-bottom-16 pcu-border-bottom pcu-flex pcu-justify-between pcu-items-center">
          <h1 className="pcu-font-22 pcu-font-700 pcu-color-title pcu-margin-0">
            {header}
          </h1>
          <div className="pcu-flex pcu-gap-12 pcu-items-center">
            <Input
              placeholder="Search by product..."
              value={searchProduct}
              onChange={(e) => setSearchProduct(e.target.value)}
              style={{ width: 200, height: 40, borderRadius: 8 }}
            />
            <Input
              placeholder="Search by seller..."
              value={searchSeller}
              onChange={(e) => setSearchSeller(e.target.value)}
              style={{ width: 200, height: 40, borderRadius: 8 }}
            />
            <Button
                icon={<ReloadOutlined />}
                onClick={fetchInventories}
                style={{ height: 40, borderRadius: 8 }}
            >
                Reset
            </Button>
            <Button
                type="primary"
                onClick={openCreateModal}
                style={{ height: 40, borderRadius: 8, fontWeight: 500, background: 'orange', borderColor: 'orange' }}
            >
                <PlusOutlined /> Add Inventory
            </Button>
          </div>
        </div>

        <div className="pcu-padding-24">
          <Table
            className="custom-table table-container"
            columns={columns}
            dataSource={filteredData}
            bordered
            pagination={{
              position: ['bottomRight'],
              showTotal: (total) => <span className="pagination-total">Total {total} items</span>
            }}
            loading={loading}
            locale={{
              emptyText: (
                <EmptyState
                  title="No Inventory Found"
                  description={(searchProduct || searchSeller) ? "No inventory matches your search." : "No seller inventory records found."}
                  gradient="blue"
                  onAction={!(searchProduct || searchSeller) ? openCreateModal : undefined}
                  actionText="Add Inventory"
                  icon="inbox"
                />
              )
            }}
          />

          <Modal title={isEditing ? "Edit Inventory" : "Add Inventory"} open={modalOpen} onOk={handleSubmit} onCancel={() => setModalOpen(false)} okText="Save">
            <Form form={form} layout="vertical">
              <Form.Item name="productId" label="Product" rules={[{ required: true, message: "Select a product" }]}>
                <Select placeholder="Select product">
                  {(productsList || []).map((p) => (
                    <Select.Option key={p._id || p.id} value={p._id || p.id}>
                      {p.name || p.title || p.productName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="sellerId" label="Seller / Shop" rules={[{ required: true, message: "Select a seller/shop" }]}>
                <Select placeholder="Select seller/shop">
                  {(shopsList || []).map((s) => (
                    <Select.Option key={s._id || s.id} value={s._id || s.id}>
                      {s.name || s.shopName || s.title}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="stock" label="Stock" rules={[{ required: true, message: "Enter stock" }]}>
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item name="price" label="Price" rules={[{ required: true, message: "Enter price" }]}>
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Form>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default SellerInventory;
